"use client";

import { useState, useEffect } from "react";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TandMList from "./TimeAndMaterialList";
import TandMForm from "./TimeAndMaterialForm";
import TandMView from "./TimeAndMaterialView";
// Version 2 components
import TandMList2 from "./TimeAndMaterialList2";
import TandMForm2 from "./TimeAndMaterialForm2";
import TandMView2 from "./TimeAndMaterialView2";
import AlertDialog from "./AlertDialog";

export default function TimeAndMaterialTab({ project, setProject }) {
  const navigate = useNavigate();
  const [timeAndMaterials, setTimeAndMaterials] = useState([]);
  const [activeTab, setActiveTab] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [preparedBy, setPreparedBy] = useState(null);

  // Form data - different structure for v1 vs v2
  const [formData, setFormData] = useState({
    // v1 structure
    labourItems: [],
    materialItems: [],
    surchargeItems: [],
    // v2 structure
    items: [],
    // Common fields
    scopeOfWork: "",
    signature: null,
    signedBy: "",
    overheadPercentage: 0,
  });

  const [availableLabourItems, setAvailableLabourItems] = useState([]);
  const [availableMaterialItems, setAvailableMaterialItems] = useState([]);
  const [selectedLabourItem, setSelectedLabourItem] = useState(null);
  const [selectedMaterialItem, setSelectedMaterialItem] = useState(null);
  const [labourQty, setLabourQty] = useState("");
  const [materialQty, setMaterialQty] = useState("");
  const [images, setImages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assemblyOptions, setAssemblyOptions] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLabourBidArea, setSelectedLabourBidArea] = useState(null);
  const [
    selectedMaterialAnticipatedBidArea,
    setSelectedMaterialAnticipatedBidArea,
  ] = useState(null);
  const [selectedLabourCostCode, setSelectedLabourCostCode] = useState(null);
  const [selectedMaterialCostCode, setSelectedMaterialCostCode] =
    useState(null);
  const [bidAreaOptions, setBidAreaOptions] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  const [editingLabourItemIndex, setEditingLabourItemIndex] = useState(null);
  const [editingMaterialItemIndex, setEditingMaterialItemIndex] =
    useState(null);
  const [editLabourForm, setEditLabourForm] = useState({
    contractLabourItem: null,
    quantityUsed: "",
    bidArea: null,
    costCode: null,
    customer: true,
    budget: true,
  });
  const [editMaterialForm, setEditMaterialForm] = useState({
    budgetLineItem: null,
    quantityUsed: "",
    vendor: null,
    bidArea: null,
    costCode: null,
    customer: true,
    budget: true,
  });
  const [rejectConfirm, setRejectConfirm] = useState({
    open: false,
    entryId: null,
  });

  // Check if project is version 2
  const isVersion2 = project?.version === 2;

  const handleDeleteLabourItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      labourItems: prev.labourItems.filter((_, idx) => idx !== index),
    }));
  };

  const handleDeleteMaterialItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      materialItems: prev.materialItems.filter((_, idx) => idx !== index),
    }));
  };

  const handleDeleteSurchargeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      surchargeItems: prev.surchargeItems.filter((_, idx) => idx !== index),
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!project?._id) {
        console.warn("Project ID is missing, skipping fetchData");
        setAvailableLabourItems([]);
        setAvailableMaterialItems([]);
        setBidAreaOptions([]);
        setAssemblyOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        // Base fetches
        const fetchPromises = [
          axios.get(
            isVersion2
              ? `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material2/${project._id}`
              : `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}`,
            { headers }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/costCodes",
            { headers }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile",
            { headers }
          ),
          // Fetch contract labour items — critical for both versions
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/projects/${project._id}/contract-labour-items`,
            { headers }
          ),
        ];

        // Add T&M options for version 2
        if (isVersion2) {
          fetchPromises.push(
            axios
              .get(
                `https://erp-production-72da01c8e651.herokuapp.com/api/assemblies/project/${project._id}/tandm-options`,
                { headers }
              )
              .catch((err) => {
                console.warn("Failed to fetch T&M options:", err);
                return { data: { conditions: [], bidAreas: [] } };
              })
          );
        }

        const responses = await Promise.all(fetchPromises);

        const [
          tmResponse,
          vendorsResponse,
          costCodesResponse,
          companyProfileResponse,
          contractLabourResponse,
          tandmOptionsResponse, // only exists if isVersion2
        ] = responses;

        // Process T&M entries - different structure for v1 vs v2
        let validatedData;
        if (isVersion2) {
          // V2 uses unified items array
          validatedData = Array.isArray(tmResponse.data)
            ? tmResponse.data.map((entry) => ({
                ...entry,
                items: Array.isArray(entry.items)
                  ? entry.items.map((item) => ({
                      ...item,
                      amount: item.amount ?? 0,
                      totalCost: item.amount ?? item.totalCost ?? 0,
                      vendor: item.vendor?._id || item.vendor || null,
                      bidArea: item.bidArea || "",
                      forCustomer: item.forCustomer !== false,
                      forBudget: item.forBudget !== false,
                    }))
                  : [],
                overheadPercentage: entry.overheadPercentage || 0,
              }))
            : [];
        } else {
          // V1 uses separate labourItems, materialItems, surchargeItems
          validatedData = Array.isArray(tmResponse.data)
            ? tmResponse.data.map((entry) => ({
                ...entry,
                materialItems: Array.isArray(entry.materialItems)
                  ? entry.materialItems.map((item) => ({
                      ...item,
                      totalCost: item.totalCost ?? 0,
                      totalPrice: item.totalPrice ?? 0,
                      vendor: item.vendor?._id || item.vendor || null,
                      bidArea: item.bidArea || "",
                      costCode: item.costCode?._id || item.costCode || null,
                      customer: item.customer ?? true,
                      budget: item.budget ?? true,
                    }))
                  : [],
                labourItems: Array.isArray(entry.labourItems)
                  ? entry.labourItems.map((item) => ({
                      ...item,
                      bidArea: item.bidArea || "",
                      costCode: item.costCode?._id || item.costCode || null,
                      customer: item.customer ?? true,
                      budget: item.budget ?? true,
                    }))
                  : [],
                surchargeItems: Array.isArray(entry.surchargeItems)
                  ? entry.surchargeItems.map((item) => ({
                      ...item,
                      totalCost: item.totalCost ?? 0,
                      totalPrice: item.totalPrice ?? 0,
                      bidArea: item.bidArea || "",
                      costCode: item.costCode?._id || item.costCode || null,
                      customer: item.customer ?? true,
                      budget: item.budget ?? true,
                    }))
                  : [],
                overheadPercentage: entry.overheadPercentage || 0,
              }))
            : [];
        }

        setTimeAndMaterials(validatedData);
        setCostCodes(costCodesResponse.data || []);
        setCompanyProfile(companyProfileResponse.data || {});

        const unifiedBudgetItems = [];

        // === CONTRACT LABOUR ITEMS ===
        const contractLabourItems = Array.isArray(contractLabourResponse.data)
          ? contractLabourResponse.data
          : [];

        // === T&M OPTIONS (for dropdowns in v2) ===
        const tandmOptions = isVersion2
          ? tandmOptionsResponse?.data || { conditions: [], bidAreas: [] }
          : { conditions: [], bidAreas: [] };

        setAssemblyOptions(
          tandmOptions.conditions.map((c) => ({ value: c, label: c }))
        );
        setBidAreaOptions(
          tandmOptions.bidAreas.map((b) => ({ value: b, label: b }))
        );

        // === BUILD availableLabourItems ===
        const labourOptions = [];

        // 1. Contract Labor — always first, for both v1 and v2
        contractLabourItems.forEach((item) => {
          unifiedBudgetItems.push({
            _id: item._id,
            category: "L",
            itemCode: item.labourItem?.itemCode || `CL-${item._id.slice(-8)}`,
            itemDescription:
              item.title || item.labourItem?.title || "Contract Labor",
            costCodeDescription: item.title || item.labourItem?.title || "",
            sectionNumber: item.costCode?.code?.split("-")[0]?.trim() || "",
            conditionName: "", // no assembly
            bidArea: "",
            uom: item.uom || item.labourItem?.uom || "HR",
            unitRate: item.unitCost || item.labourItem?.unitCost || 0,
            unitCost: item.unitCost || item.labourItem?.unitCost || 0,
            remainingQty: item.quantity || 0,
            budgetedQty: item.quantity || 0,
            size: "",
            isContractLabor: true,
            contractLabourItemId: item._id,
          });
        });

        // 2. Version 1: Labor from budgets
        if (!isVersion2 && Array.isArray(project?.budgets)) {
          project.budgets.forEach((budget) => {
            if (Array.isArray(budget?.lineItems)) {
              budget.lineItems.forEach((item) => {
                if (item.category === "M") {
                  unifiedBudgetItems.push({
                    _id: item._id,
                    category: "M",
                    itemCode: item.itemCode || item.materialCode || "",
                    itemDescription: item.itemDescription || "",
                    costCodeDescription: item.costCodeDescription || "",
                    conditionName: item.conditionName || "",
                    sectionNumber: item.sectionNumber || "",
                    bidArea: item.bidArea || "",
                    uom: item.container || "",
                    unitCost: item.cost || 0,
                    remainingQty: item.remaining || 0,
                    budgetedQty: item.requiredQty || 0,
                    size: item.size || "",
                    vendor: item.vendor,
                  });
                } else if (item.category === "L") {
                  unifiedBudgetItems.push({
                    _id: item._id,
                    category: "L",
                    itemCode: item.laborId || item.itemCode || "",
                    itemDescription: item.costCodeDescription || "",
                    costCodeDescription: item.costCodeDescription || "",
                    conditionName: item.conditionName || "",
                    sectionNumber: item.sectionNumber || "",
                    bidArea: item.bidArea || "",
                    uom: item.uom || "HR",
                    unitRate: item.unitRate || 0,
                    unitCost: item.unitCost || item.cost || 0,
                    remainingQty: item.remaining || 0,
                    budgetedQty: item.quantity || 0,
                    size: "",
                  });
                }
              });
            }
          });
        }

        setAvailableLabourItems(unifiedBudgetItems);

        // === MATERIALS (keep your existing logic for v1) ===
        const seenMaterialLabels = new Set();
        const materialOptions = [];

        if (!isVersion2 && Array.isArray(project?.budgets)) {
          project.budgets.forEach((budget) => {
            if (Array.isArray(budget?.lineItems)) {
              budget.lineItems
                .filter((item) => item.category === "M")
                .forEach((item) => {
                  const label = `${item.itemDescription || "Material"} ($${
                    item.cost || 0
                  } / ${item.container || "EA"}) - ${item.size || "N/A"}`;
                  if (!seenMaterialLabels.has(label)) {
                    seenMaterialLabels.add(label);
                    materialOptions.push({
                      value: item._id,
                      label,
                      size: item.size || "N/A",
                      cost: item.cost || 0,
                      price: item.price || 0,
                      container: item.container || "EA",
                      itemDescription: item.itemDescription || "Material",
                      vendor: item.vendor?._id || item.vendor || null,
                      bidArea: item.bidArea || "",
                      costCode: item.costCode?._id || item.costCode || null,
                      itemCode: item.itemCode || "N/A",
                    });
                  }
                });
            }
          });
        }

        setAvailableMaterialItems(materialOptions);

        // Vendors
        const vendorData = Array.isArray(vendorsResponse.data)
          ? vendorsResponse.data.filter((v) => v.type === "vendor")
          : [];
        setVendors(vendorData);

        setLoading(false);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to load data.",
        });
        console.error("Error in fetchData:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [project, isVersion2]);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  const costCodeOptions = costCodes.map((costCode) => ({
    value: costCode._id,
    label: `${costCode.code} - ${costCode.description}`,
  }));

  const handleCreateToggle = () => {
    setIsCreating(!isCreating);
    setIsEditing(false);
    setEditingId(null);
    setViewMode("list");
    setSelectedEntry(null);

    // Reset form data based on version
    setFormData({
      labourItems: [],
      materialItems: [],
      surchargeItems: [],
      items: [],
      scopeOfWork: "",
      signature: null,
      signedBy: "",
      overheadPercentage: companyProfile?.defaultOverhead || 0,
    });

    setSelectedLabourItem(null);
    setSelectedMaterialItem(null);
    setLabourQty("");
    setMaterialQty("");
    setSelectedVendor(null);
    setSelectedLabourBidArea(null);
    setSelectedMaterialAnticipatedBidArea(null);
    setSelectedLabourCostCode(null);
    setSelectedMaterialCostCode(null);
    setImages([]);
    setPreparedBy(null);
    setEditingLabourItemIndex(null);
    setEditingMaterialItemIndex(null);
    setAlert({ open: false, type: "error", title: "", message: "" });
  };

  const handleEdit = (entry) => {
    if (entry.status !== "Pending" && entry.status !== "Rejected") {
      setAlert({
        open: true,
        type: "error",
        title: "Cannot Edit",
        message: "Can only edit Pending or Rejected entries.",
      });
      return;
    }

    setIsEditing(true);
    setIsCreating(true);
    setEditingId(entry._id);
    setViewMode("edit");

    try {
      if (isVersion2) {
        // V2: Convert unified items array to form data format
        const items = (entry.items || []).map((item, index) => ({
          _localId: item._id || `item-${Date.now()}-${index}`,
          itemNumber: item.itemNumber || String(index + 1),
          sourceAssemblyId: item.sourceAssemblyId || null,
          sourceLineItemId: item.sourceLineItemId || null,
          contractLabourItemId: item.contractLabourItemId || null,
          itemCode: item.itemCode || "",
          category: item.category || "M",
          sectionNumber: item.sectionNumber || "",
          title: item.title || item.itemDescription || "",
          description: item.title || item.itemDescription || "",
          customerDescription:
            item.customerDescription || item.itemDescription || "",
          conditionName: item.conditionName || "",
          bidArea: item.bidArea || "",
          deltaQty: item.deltaQty || item.quantity || 0,
          quantityUsed: item.quantity || item.deltaQty || 0,
          unitCost: item.unitCost || 0,
          totalCost: Math.abs(item.amount || item.totalCost || 0),
          amount: Math.abs(item.amount || item.totalCost || 0),
          size: item.size || "",
          uom: item.uom || "EA",
          coveragePerUnit: item.coveragePerUnit || "1",
          coverage: parseFloat(item.coveragePerUnit) || 1,
          vendor: item.vendor?._id || item.vendor || null,
          forCustomer: item.forCustomer !== false,
          forBudget: item.forBudget !== false,
          referenceNumber: item.referenceNumber || null,
        }));

        setFormData({
          items,
          labourItems: [],
          materialItems: [],
          surchargeItems: [],
          scopeOfWork: entry.scopeOfWork || "",
          signature: entry.signature || null,
          signedBy: entry.signedBy || "",
          overheadPercentage: entry.overheadPercentage || 0,
        });
      } else {
        // V1: Keep existing logic
        setFormData({
          items: [],
          labourItems: entry.labourItems.map((item) => ({
            contractLabourItem:
              typeof item.contractLabourItem === "object" &&
              item.contractLabourItem?._id
                ? item.contractLabourItem._id
                : item.contractLabourItem || null,
            budgetLineItem:
              typeof item.budgetLineItem === "object" &&
              item.budgetLineItem?._id
                ? item.budgetLineItem._id
                : item.budgetLineItem || null,
            quantityUsed: item.quantityUsed || 0,
            totalPrice: item.totalPrice || 0,
            totalCost: item.totalCost || 0,
            bidArea: item.bidArea || "",
            costCode: item.costCode?._id || item.costCode || null,
            customer: item.customer ?? true,
            budget: item.budget ?? true,
            unitRate: item.unitRate || 0,
            unitCost: item.unitCost || 0,
            uom: item.uom || "N/A",
            assemblyId: item.assemblyId || null,
            assemblyName: item.assemblyName || null,
          })),
          materialItems: entry.materialItems.map((item) => {
            const materialOption = availableMaterialItems.find(
              (opt) =>
                opt.value ===
                (typeof item.budgetLineItem === "object"
                  ? item.budgetLineItem?._id
                  : item.budgetLineItem)
            );
            return {
              budgetLineItem:
                typeof item.budgetLineItem === "object" &&
                item.budgetLineItem?._id
                  ? item.budgetLineItem._id
                  : item.budgetLineItem || null,
              quantityUsed: item.quantityUsed || 0,
              totalCost: item.totalCost || 0,
              totalPrice: item.totalPrice || 0,
              price: item.price || 0,
              cost: item.cost || 0,
              size: item.size || materialOption?.size || "N/A",
              itemCode: item.itemCode || materialOption?.itemCode || "N/A",
              itemDescription:
                item.itemDescription ||
                materialOption?.itemDescription ||
                "Unnamed",
              container: item.container || materialOption?.container || "N/A",
              vendor: item.vendor?._id || item.vendor || null,
              bidArea: item.bidArea || "",
              costCode: item.costCode?._id || item.costCode || null,
              customer: item.customer ?? true,
              budget: item.budget ?? true,
              assemblyId: item.assemblyId || null,
              assemblyName: item.assemblyName || null,
            };
          }),
          surchargeItems: (entry.surchargeItems || []).map((item) => ({
            title: item.title || "",
            quantityUsed: item.quantityUsed || 0,
            unitCost: item.unitCost || 0,
            unitRate: item.unitRate || 0,
            totalCost: item.totalCost || 0,
            totalPrice: item.totalPrice || 0,
            costCode: item.costCode?._id || item.costCode || null,
            bidArea: item.bidArea || "",
            customer: item.customer ?? true,
            budget: item.budget ?? true,
            assemblyId: item.assemblyId || null,
            assemblyName: item.assemblyName || null,
          })),
          scopeOfWork: entry.scopeOfWork || "",
          signature: entry.signature || null,
          signedBy: entry.signedBy || "",
          overheadPercentage: entry.overheadPercentage || 0,
        });
      }

      setPreparedBy(entry.createdBy?.displayName || "");
      setImages(entry.images || []);
      setAlert({ open: false, type: "error", title: "", message: "" });
    } catch (err) {
      console.error("Error in handleEdit:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load entry for editing.",
      });
    }
  };

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setViewMode("view");
  };

  const generateTandMPDF = async (formDataOverride = null) => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot generate PDF: Company profile is not set.",
      });
      return;
    }

    const entry = formDataOverride || selectedEntry;

    if (!entry) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "No entry or form data available to generate PDF.",
      });
      return;
    }

    // Handle both v1 and v2 structures
    let labourItems, materialItems, surchargeItems;

    if (isVersion2 || entry.items) {
      // V2: Convert unified items to separate arrays for PDF generation
      // Only include items with forCustomer = true in the PDF
      const items = entry.items || [];
      labourItems = items.filter(
        (i) => i.category === "L" && i.forCustomer !== false
      );
      materialItems = items.filter(
        (i) => i.category === "M" && i.forCustomer !== false
      );
      surchargeItems = items.filter(
        (i) => i.category === "S" && i.forCustomer !== false
      );
    } else {
      // V1: Use existing structure
      labourItems = Array.isArray(entry.labourItems) ? entry.labourItems : [];
      materialItems = Array.isArray(entry.materialItems)
        ? entry.materialItems
        : [];
      surchargeItems = Array.isArray(entry.surchargeItems)
        ? entry.surchargeItems
        : [];
    }

    try {
      const response = await fetch("/t_and_m_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64))
          throw new Error("Invalid base64 data");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: (img, tagValue, tagName) => {
          if (tagName === "companyLogo") return [150, 100];
          if (tagName === "signature") return [100, 150];
          if (tagName === "image") return [200, 200];
          return [150, 100];
        },
      };
      const imageModule = new ImageModule(imageOptions);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule],
      });

      let companyLogoBase64 = null;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        try {
          const logoResponse = await fetch(companyProfile.logo, {
            mode: "cors",
          });
          if (!logoResponse.ok)
            throw new Error(`Failed to fetch logo: ${logoResponse.statusText}`);
          const logoBlob = await logoResponse.blob();
          companyLogoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        } catch (err) {
          console.error("Error fetching company logo:", err.message);
          companyLogoBase64 = null;
        }
      } else if (companyProfile?.logo) {
        companyLogoBase64 = companyProfile.logo;
      }

      let signatureBase64 = null;
      if (entry.signature) {
        try {
          const sigResponse = await fetch(
            entry.signature instanceof File
              ? URL.createObjectURL(entry.signature)
              : entry.signature,
            { mode: "cors" }
          );
          if (!sigResponse.ok)
            throw new Error(
              `Failed to fetch signature: ${sigResponse.statusText}`
            );
          const sigBlob = await sigResponse.blob();
          signatureBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(sigBlob);
          });
        } catch (err) {
          console.warn("Failed to fetch signature:", err.message);
          signatureBase64 = null;
        }
      }

      const imagesPromises = (entry.images || []).map(async (url, index) => {
        let imageBase64 = null;
        try {
          const imgResponse = await fetch(
            url instanceof File ? URL.createObjectURL(url) : url,
            { mode: "cors" }
          );
          if (!imgResponse.ok)
            throw new Error(`Failed to fetch image: ${imgResponse.statusText}`);
          const imgBlob = await imgResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(imgBlob);
          });
        } catch (err) {
          console.warn(`Failed to fetch image ${index + 1}: ${err.message}`);
          imageBase64 = null;
        }
        return { image: imageBase64 };
      });

      const resolvedImages = await Promise.all(imagesPromises);

      // Process items for PDF - handle both v1 and v2 structures
      // For v2, use forCustomer to determine what goes in PDF
      const pdfLabourItems = labourItems
        .filter(
          (item) =>
            item &&
            (isVersion2 ? item.forCustomer !== false : item.customer === true)
        )
        .map((item, index) => {
          let description, unitPrice, amount, quantity;

          if (isVersion2) {
            description =
              item.title || item.itemDescription || "Unnamed Labour Item";
            unitPrice = item.unitCost || 0;
            amount = Math.abs(item.amount || item.totalCost || 0);
            quantity = item.quantity || item.deltaQty || 0;
          } else {
            const labourOption = availableLabourItems.find(
              (opt) =>
                opt.value ===
                (item.contractLabourItem?._id ||
                  item.contractLabourItem ||
                  item.budgetLineItem?._id ||
                  item.budgetLineItem)
            );
            description = labourOption
              ? labourOption.label.split(" (")[0]
              : item.contractLabourItem?.labourItem?.title ||
                item.budgetLineItem?.costCodeDescription ||
                item.costCodeDescription ||
                "Unnamed Labour Item";
            unitPrice =
              item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
            amount = item.totalPrice || 0;
            quantity = item.quantityUsed || 0;
          }

          return {
            itemNo: index + 1,
            description,
            quantity,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      const pdfMaterialItems = materialItems
        .filter(
          (item) =>
            item &&
            (isVersion2 ? item.forCustomer !== false : item.customer === true)
        )
        .map((item, index) => {
          let description, unitPrice, amount, quantity;

          if (isVersion2) {
            description = `${
              item.title || item.itemDescription || "Unnamed"
            } (${item.uom || "EA"} - ${item.size || "N/A"})`;
            unitPrice = item.unitCost || 0;
            amount = Math.abs(item.amount || item.totalCost || 0);
            quantity = item.quantity || item.deltaQty || 0;
          } else {
            const materialOption = availableMaterialItems.find(
              (opt) =>
                opt.value === (item.budgetLineItem?._id || item.budgetLineItem)
            );
            description = `${
              item.itemDescription ||
              materialOption?.itemDescription ||
              "Unnamed"
            } (${item.container || materialOption?.container || "N/A"} - ${
              item.size || materialOption?.size || "N/A"
            })`;
            unitPrice =
              item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
            amount = item.totalPrice || 0;
            quantity = item.quantityUsed || 0;
          }

          return {
            itemNo: pdfLabourItems.length + index + 1,
            description,
            quantity,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      const pdfSurchargeItems = surchargeItems
        .filter(
          (item) =>
            item &&
            (isVersion2 ? item.forCustomer !== false : item.customer === true)
        )
        .map((item, index) => {
          let unitPrice, amount, quantity;

          if (isVersion2) {
            unitPrice = item.unitCost || 0;
            amount = Math.abs(item.amount || item.totalCost || 0);
            quantity = item.quantity || item.deltaQty || 0;
          } else {
            unitPrice =
              item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
            amount = item.totalPrice || 0;
            quantity = item.quantityUsed || 0;
          }

          return {
            itemNo: pdfLabourItems.length + pdfMaterialItems.length + index + 1,
            description: item.title || "Unnamed Surcharge",
            quantity,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: amount.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      const items = [
        ...pdfLabourItems,
        ...pdfMaterialItems,
        ...pdfSurchargeItems,
      ];

      if (items.length === 0) {
        console.warn("No items to include in PDF.");
      }

      // Calculate financials
      const subtotalPrice = items.reduce((sum, item) => {
        const amount = parseFloat(item.amount.replace(/[^0-9.-]+/g, "")) || 0;
        return sum + amount;
      }, 0);
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const taxAmount = subtotalPrice * (taxRate / 100);
      const totalPrice = subtotalPrice + taxAmount;

      const data = {
        projectName: project.projectName || "Unknown Project",
        date: formDataOverride
          ? new Date().toLocaleDateString()
          : new Date(entry.signedAt || Date.now()).toLocaleDateString(),
        preparedBy: formDataOverride
          ? preparedBy || "Unknown"
          : entry.createdBy?.displayName || "Unknown",
        timeAndMaterialNumber: entry.timeAndMaterialNumber || "Draft",
        scopeOfWork: entry.scopeOfWork || "N/A",
        signedBy: entry.signedBy || "N/A",
        companyName: companyProfile?.companyName || "Your Company Name",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        companyLogo: companyLogoBase64 || null,
        signature: signatureBase64 || null,
        items,
        images: resolvedImages,
        subtotalPrice: subtotalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        taxRate: taxRate.toFixed(2),
        taxAmount: taxAmount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        totalPrice: totalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        overheadPercentage: entry.overheadPercentage
          ? entry.overheadPercentage.toFixed(2)
          : "0.00",
      };

      doc.setData(data);
      try {
        doc.render();
      } catch (renderError) {
        console.error("Error rendering Docxtemplater:", renderError);
        throw new Error("Failed to render document template.");
      }

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `TandM_${entry._id || "draft"}.docx`
      );
      formDataPDF.append("documentType", "TandM");
      formDataPDF.append("identifier", entry._id || "draft");

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formDataPDF,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        }
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        saveAs(pdfBlob, `TandM_${entry._id || "draft"}.pdf`);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "T&M PDF generated successfully!",
        });
      } else {
        throw new Error("Failed to convert DOCX to PDF");
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
      });
      console.error("Error in generateTandMPDF:", err);
    }
  };

  const handleStatusChange = async (entryId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const entry = timeAndMaterials.find((item) => item._id === entryId);

      if (newStatus === "Rejected" && entry.status === "Approved") {
        setRejectConfirm({ open: true, entryId });
        return;
      }

      const endpoint = isVersion2
        ? `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material2/${project._id}/${entryId}/status`
        : `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${entryId}/status`;

      const response = await axios.patch(
        endpoint,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedEntry = isVersion2
        ? response.data.tandm
        : response.data.timeAndMaterial;

      setTimeAndMaterials((prev) =>
        prev.map((item) => (item._id === entryId ? updatedEntry : item))
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: `Status updated to ${newStatus}.`,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to update status to ${newStatus}.`,
      });
      console.error("Error:", err);
    }
  };

  const confirmReject = async () => {
    try {
      const token = localStorage.getItem("token");

      const endpoint = isVersion2
        ? `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material2/${project._id}/${rejectConfirm.entryId}/status`
        : `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${rejectConfirm.entryId}/status`;

      const response = await axios.patch(
        endpoint,
        { status: "Rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedEntry = isVersion2
        ? response.data.tandm
        : response.data.timeAndMaterial;

      setTimeAndMaterials((prev) =>
        prev.map((item) =>
          item._id === rejectConfirm.entryId ? updatedEntry : item
        )
      );
      setRejectConfirm({ open: false, entryId: null });
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "T&M entry rejected successfully.",
      });
    } catch (err) {
      setRejectConfirm({ open: false, entryId: null });
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to reject T&M entry.",
      });
      console.error("Error:", err);
    }
  };

  const handleConvertToChangeOrder = async (entry) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${entry._id}/convert-to-change-order`,
        { title: `Change Order from T&M #${entry.timeAndMaterialNumber}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const changeOrderId =
        response.data.changeOrders[response.data.changeOrders.length - 1]._id;
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Converted to Change Order successfully!",
        onConfirm: () => {
          setAlert({ open: false, type: "error", title: "", message: "" });
          navigate(
            `/projects/${project._id}/change-orders/${changeOrderId}/edit`
          );
        },
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to convert to Change Order.",
      });
      console.error("Error:", err);
    }
  };

  const filteredTimeAndMaterials = timeAndMaterials.filter(
    (entry) =>
      (activeTab === "Pending" && entry.status === "Pending") ||
      (activeTab === "Approved" && entry.status === "Approved") ||
      (activeTab === "Rejected" && entry.status === "Rejected")
  );

  // Calculate totals based on version
  let labourSubtotalCost,
    labourSubtotalPrice,
    materialSubtotalCost,
    materialSubtotalPrice;
  let surchargeSubtotalCost, surchargeSubtotalPrice;

  if (isVersion2) {
    // V2: Calculate from unified items array
    // Cost = items for budget, Price = items for customer
    const items = formData.items || [];
    const labourItems = items.filter((i) => i.category === "L");
    const materialItems = items.filter((i) => i.category === "M");
    const surchargeItems = items.filter((i) => i.category === "S");

    // Budget totals (forBudget)
    labourSubtotalCost = labourItems.reduce(
      (sum, item) =>
        sum +
        (item.forBudget !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
    // Customer totals (forCustomer)
    labourSubtotalPrice = labourItems.reduce(
      (sum, item) =>
        sum +
        (item.forCustomer !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
    materialSubtotalCost = materialItems.reduce(
      (sum, item) =>
        sum +
        (item.forBudget !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
    materialSubtotalPrice = materialItems.reduce(
      (sum, item) =>
        sum +
        (item.forCustomer !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
    surchargeSubtotalCost = surchargeItems.reduce(
      (sum, item) =>
        sum +
        (item.forBudget !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
    surchargeSubtotalPrice = surchargeItems.reduce(
      (sum, item) =>
        sum +
        (item.forCustomer !== false
          ? Math.abs(item.totalCost || item.amount || 0)
          : 0),
      0
    );
  } else {
    // V1: Calculate from separate arrays
    labourSubtotalCost = formData.labourItems.reduce(
      (sum, item) => sum + (item.budget ? item.totalCost || 0 : 0),
      0
    );
    labourSubtotalPrice = formData.labourItems.reduce(
      (sum, item) => sum + (item.customer ? item.totalPrice || 0 : 0),
      0
    );
    materialSubtotalCost = formData.materialItems.reduce(
      (sum, item) => sum + (item.budget ? item.totalCost || 0 : 0),
      0
    );
    materialSubtotalPrice = formData.materialItems.reduce(
      (sum, item) => sum + (item.customer ? item.totalPrice || 0 : 0),
      0
    );
    surchargeSubtotalCost = (formData.surchargeItems || []).reduce(
      (sum, item) => sum + (item.budget ? item.totalCost || 0 : 0),
      0
    );
    surchargeSubtotalPrice = (formData.surchargeItems || []).reduce(
      (sum, item) => sum + (item.customer ? item.totalPrice || 0 : 0),
      0
    );
  }

  const subtotalCost =
    labourSubtotalCost + materialSubtotalCost + surchargeSubtotalCost;
  const subtotalPrice =
    labourSubtotalPrice + materialSubtotalPrice + surchargeSubtotalPrice;
  const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
  const taxAmount = subtotalPrice * (taxRate / 100);
  const totalPrice = subtotalPrice + taxAmount;

  if (loading) {
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  }

  // Common props for both versions
  const commonFormProps = {
    isEditing,
    editingId,
    formData,
    setFormData,
    generateTandMPDF,
    images,
    setImages,
    handleCreateToggle,
    companyProfile,
    preparedBy,
    setAlert,
    vendorOptions,
    costCodeOptions,
    subtotalCost,
    subtotalPrice,
    taxRate,
    taxAmount,
    totalPrice,
    project,
    setTimeAndMaterials,
    setProject,
    setIsCreating,
    setIsEditing,
    setEditingId,
    setViewMode,
    bidAreaOptions,
    assemblyOptions,
    setAssemblyOptions,
    handleDeleteLabourItem,
    handleDeleteMaterialItem,
    handleDeleteSurchargeItem,
  };

  const commonListProps = {
    activeTab,
    setActiveTab,
    filteredTimeAndMaterials,
    handleCreateToggle,
    handleEdit,
    handleView,
    handleStatusChange,
    handleConvertToChangeOrder,
    costCodeOptions,
    availableLabourItems,
    vendorOptions,
  };

  const commonViewProps = {
    selectedEntry,
    companyProfile,
    costCodeOptions,
    vendorOptions,
    bidAreaOptions,
    setViewMode,
    generateTandMPDF,
    project,
    setTimeAndMaterials,
    setProject,
    setIsCreating,
    setParentIsEditing: setIsEditing,
    setEditingId,
    assemblyOptions,
    setAssemblyOptions,
  };

  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          confirmText="OK"
          onClose={() =>
            setAlert({ open: false, type: "error", title: "", message: "" })
          }
          onConfirm={
            alert.onConfirm ||
            (() =>
              setAlert({ open: false, type: "error", title: "", message: "" }))
          }
        />
        <AlertDialog
          open={rejectConfirm.open}
          type="warning"
          title="Confirm Rejection"
          message="Rejecting this approved T&M will reverse the entry and remove associated items from purchase orders and budgets. This action cannot be undone. Are you sure you want to proceed?"
          confirmText="Reject"
          cancelText="Cancel"
          onClose={() => setRejectConfirm({ open: false, entryId: null })}
          onConfirm={confirmReject}
        />

        {viewMode === "view" && selectedEntry ? (
          isVersion2 ? (
            <TandMView2
              {...commonViewProps}
              availableLabourItems={availableLabourItems}
              availableMaterialItems={availableMaterialItems}
            />
          ) : (
            <TandMView
              {...commonViewProps}
              availableLabourItems={availableLabourItems}
              availableMaterialItems={availableMaterialItems}
              formData={formData}
              setFormData={setFormData}
              selectedLabourItem={selectedLabourItem}
              setSelectedLabourItem={setSelectedLabourItem}
              selectedMaterialItem={selectedMaterialItem}
              setSelectedMaterialItem={setSelectedMaterialItem}
              labourQty={labourQty}
              setLabourQty={setLabourQty}
              materialQty={materialQty}
              setMaterialQty={setMaterialQty}
              images={images}
              setImages={setImages}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              selectedLabourBidArea={selectedLabourBidArea}
              setSelectedLabourBidArea={setSelectedLabourBidArea}
              selectedMaterialAnticipatedBidArea={
                selectedMaterialAnticipatedBidArea
              }
              setSelectedMaterialAnticipatedBidArea={
                setSelectedMaterialAnticipatedBidArea
              }
              selectedLabourCostCode={selectedLabourCostCode}
              setSelectedLabourCostCode={setSelectedLabourCostCode}
              selectedMaterialCostCode={selectedMaterialCostCode}
              setSelectedMaterialCostCode={setSelectedMaterialCostCode}
              editingLabourItemIndex={editingLabourItemIndex}
              setEditingLabourItemIndex={setEditingLabourItemIndex}
              editingMaterialItemIndex={editingMaterialItemIndex}
              setEditingMaterialItemIndex={setEditingMaterialItemIndex}
              editLabourForm={editLabourForm}
              setEditLabourForm={setEditLabourForm}
              editMaterialForm={editMaterialForm}
              setEditMaterialForm={setEditMaterialForm}
            />
          )
        ) : isCreating ? (
          isVersion2 ? (
            <TandMForm2
              {...commonFormProps}
              availableLabourItems={availableLabourItems}
              availableMaterialItems={availableMaterialItems}
              selectedLabourItem={selectedLabourItem}
              setSelectedLabourItem={setSelectedLabourItem}
              selectedMaterialItem={selectedMaterialItem}
              setSelectedMaterialItem={setSelectedMaterialItem}
              labourQty={labourQty}
              setLabourQty={setLabourQty}
              materialQty={materialQty}
              setMaterialQty={setMaterialQty}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              selectedLabourBidArea={selectedLabourBidArea}
              setSelectedLabourBidArea={setSelectedLabourBidArea}
              selectedMaterialAnticipatedBidArea={
                selectedMaterialAnticipatedBidArea
              }
              setSelectedMaterialAnticipatedBidArea={
                setSelectedMaterialAnticipatedBidArea
              }
              selectedLabourCostCode={selectedLabourCostCode}
              setSelectedLabourCostCode={setSelectedLabourCostCode}
              selectedMaterialCostCode={selectedMaterialCostCode}
              setSelectedMaterialCostCode={setSelectedMaterialCostCode}
              editingLabourItemIndex={editingLabourItemIndex}
              setEditingLabourItemIndex={setEditingLabourItemIndex}
              editingMaterialItemIndex={editingMaterialItemIndex}
              setEditingMaterialItemIndex={setEditingMaterialItemIndex}
              editLabourForm={editLabourForm}
              setEditLabourForm={setEditLabourForm}
              editMaterialForm={editMaterialForm}
              setEditMaterialForm={setEditMaterialForm}
            />
          ) : (
            <TandMForm
              {...commonFormProps}
              availableLabourItems={availableLabourItems}
              availableMaterialItems={availableMaterialItems}
              selectedLabourItem={selectedLabourItem}
              setSelectedLabourItem={setSelectedLabourItem}
              selectedMaterialItem={selectedMaterialItem}
              setSelectedMaterialItem={setSelectedMaterialItem}
              labourQty={labourQty}
              setLabourQty={setLabourQty}
              materialQty={materialQty}
              setMaterialQty={setMaterialQty}
              vendors={vendors}
              costCodes={costCodes}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              selectedLabourBidArea={selectedLabourBidArea}
              setSelectedLabourBidArea={setSelectedLabourBidArea}
              selectedMaterialAnticipatedBidArea={
                selectedMaterialAnticipatedBidArea
              }
              setSelectedMaterialAnticipatedBidArea={
                setSelectedMaterialAnticipatedBidArea
              }
              selectedLabourCostCode={selectedLabourCostCode}
              setSelectedLabourCostCode={setSelectedLabourCostCode}
              selectedMaterialCostCode={selectedMaterialCostCode}
              setSelectedMaterialCostCode={setSelectedMaterialCostCode}
              editingLabourItemIndex={editingLabourItemIndex}
              setEditingLabourItemIndex={setEditingLabourItemIndex}
              editingMaterialItemIndex={editingMaterialItemIndex}
              setEditingMaterialItemIndex={setEditingMaterialItemIndex}
              editLabourForm={editLabourForm}
              setEditLabourForm={setEditLabourForm}
              editMaterialForm={editMaterialForm}
              setEditMaterialForm={setEditMaterialForm}
            />
          )
        ) : isVersion2 ? (
          <TandMList2 {...commonListProps} assemblyOptions={assemblyOptions} />
        ) : (
          <TandMList {...commonListProps} />
        )}
      </div>
    </div>
  );
}
